import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Container from './Container';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';
import { hero, site } from '@/lib/content';

export default function Hero() {
  return (
    <section
      id="overview"
      aria-labelledby="hero-heading"
      className="relative overflow-hidden pb-20 pt-36 md:pb-32 md:pt-44 lg:pt-52"
    >
      {/* subtle grid + gradient wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-grid-faint [background-size:56px_56px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[70%] bg-gradient-to-b from-paper-50 via-paper to-transparent"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-accent/10 via-accent/0 to-transparent blur-3xl"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.8, delay: 0.3 }}
        className="pointer-events-none absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-gradient-to-tr from-copper-300/20 via-transparent to-transparent blur-3xl"
      />

      <Container className="relative">
        <motion.div
          variants={stagger(0.1, 0.12)}
          initial="hidden"
          animate="visible"
          className="max-w-5xl"
        >
          <motion.div variants={fadeUp} className="eyebrow mb-7 flex items-center gap-3">
            <span className="h-px w-8 bg-ink/30" />
            {hero.eyebrow}
          </motion.div>

          <motion.h1
            variants={fadeUp}
            id="hero-heading"
            className="text-balance font-display text-[2.5rem] leading-[1.02] tracking-tightest md:text-[4.25rem] lg:text-[5.25rem]"
          >
            {hero.headline}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-2xl text-pretty text-lg leading-relaxed text-ink/70 md:text-xl"
          >
            {hero.sub}
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href={hero.ctaPrimary.href}
              className="group inline-flex items-center gap-3 rounded-full bg-ink px-6 py-3.5 text-sm font-medium text-paper shadow-card transition-all hover:-translate-y-px hover:bg-ink/90 hover:shadow-card-hover"
            >
              {hero.ctaPrimary.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={hero.ctaSecondary.href}
              className="link-underline text-sm font-medium text-ink/80 hover:text-ink"
            >
              {hero.ctaSecondary.label}
            </a>
          </motion.div>
        </motion.div>

        {/* Footer meta row inside hero */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-20 flex flex-col gap-6 border-t border-ink/10 pt-8 text-sm text-ink/60 md:mt-28 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex items-center gap-4">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-copper" />
            <span>
              Operating since {site.established} · {site.cities.join(' · ')}
            </span>
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
            Scroll ↓
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
